import { LitElement, html, css } from "lit";
import { customElement } from "lit/decorators.js";
import { Router } from "@vaadin/router";
import "@awesome.me/webawesome/dist/components/icon/icon.js";
import { flowStyles } from "../styles/flow-styles";

interface PickerTile {
  key: string;
  title: string;
  subtitle: string;
  icon: string;
  accent: string; // CSS color for the icon chip
  route: string;
}

/**
 * Full-screen task picker opened by the `+` FAB. The user picks one logging
 * task; each tile routes to a focused single-task flow. See
 * docs/add-task-flows.md.
 */
@customElement("add-picker-page")
export class AddPickerPage extends LitElement {
  static styles = [
    flowStyles,
    css`
      .tiles {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 0.875rem;
        margin-top: 0.25rem;
      }

      .tile {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 0.625rem;
        padding: 1.1rem;
        background: white;
        border: 1px solid #e9ecef;
        border-radius: 1rem;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
        cursor: pointer;
        text-align: left;
        transition:
          transform 0.08s ease,
          box-shadow 0.08s ease;
      }

      .tile:active {
        transform: scale(0.97);
      }

      .tile:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
      }

      .tile-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 48px;
        height: 48px;
        border-radius: 0.875rem;
        font-size: 1.5rem;
        color: white;
      }

      .tile-title {
        font-size: 1.05rem;
        font-weight: 600;
        color: #1a1a1a;
        line-height: 1.1;
      }

      .tile-subtitle {
        font-size: 0.78rem;
        color: #868e96;
        line-height: 1.25;
      }

      /* Glucose is the primary, most-frequent action - span the full width. */
      .tile.primary {
        grid-column: 1 / -1;
        flex-direction: row;
        align-items: center;
        gap: 1rem;
      }

      .tile.primary .tile-icon {
        width: 52px;
        height: 52px;
      }

      .tile.primary .tile-text {
        display: flex;
        flex-direction: column;
        gap: 0.15rem;
      }
    `,
  ];

  private readonly _tiles: PickerTile[] = [
    {
      key: "glucose",
      title: "Glucose check",
      subtitle: "Log a reading and get DAFNE guidance",
      icon: "droplet",
      accent: "#0d6efd",
      route: "/quick-gl",
    },
    {
      key: "meal",
      title: "Meal",
      subtitle: "Carbs + quick insulin",
      icon: "utensils",
      accent: "#fd7e14",
      route: "/add/meal",
    },
    {
      key: "quick",
      title: "Quick / correction",
      subtitle: "A correction or quick dose",
      icon: "syringe",
      accent: "#d6336c",
      route: "/add/quick",
    },
    {
      key: "long",
      title: "Long insulin",
      subtitle: "Background dose",
      icon: "prescription-bottle-medical",
      accent: "#7048e8",
      route: "/add/long",
    },
    {
      key: "exercise",
      title: "Exercise",
      subtitle: "Activity you did",
      icon: "person-running",
      accent: "#0ca678",
      route: "/add/exercise",
    },
    {
      key: "ketone",
      title: "Ketone",
      subtitle: "Ketone reading",
      icon: "vial",
      accent: "#f59f00",
      route: "/add/ketone",
    },
    {
      key: "sick",
      title: "Sick day",
      subtitle: "Mark sick or recovered",
      icon: "face-thermometer",
      accent: "#e8590c",
      route: "/add/sick",
    },
  ];

  private _go(route: string) {
    Router.go(route);
  }

  private _handleBack() {
    Router.go("/");
  }

  render() {
    const [primary, ...rest] = this._tiles;
    return html`
      <div class="page-container">
        <div class="page-header">
          <button class="back-btn" @click=${this._handleBack} aria-label="Back">
            <wa-icon name="arrow-left"></wa-icon>
          </button>
          <h1>Log something</h1>
        </div>

        <div class="tiles">
          <button class="tile primary" @click=${() => this._go(primary.route)}>
            <span class="tile-icon" style="background:${primary.accent}">
              <wa-icon name=${primary.icon}></wa-icon>
            </span>
            <span class="tile-text">
              <span class="tile-title">${primary.title}</span>
              <span class="tile-subtitle">${primary.subtitle}</span>
            </span>
          </button>

          ${rest.map(
            (t) => html`
              <button class="tile" @click=${() => this._go(t.route)}>
                <span class="tile-icon" style="background:${t.accent}">
                  <wa-icon name=${t.icon}></wa-icon>
                </span>
                <span class="tile-title">${t.title}</span>
                <span class="tile-subtitle">${t.subtitle}</span>
              </button>
            `,
          )}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "add-picker-page": AddPickerPage;
  }
}
